import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AppSettings,
  CountryOption,
  PerimeterConfig,
  ProtectedUser,
  Site,
  Suspect,
  ThreatProfile,
} from '../../shared/types/index.js';
import {
  fetchCountries,
  fetchPerimeters,
  fetchProtectedUsers,
  fetchSettings,
  fetchSites,
  fetchSuspects,
} from '../api/index.js';
import type { Position } from '../api/types.js';
import { computeThreatState, type ThreatEvent, type ThreatState } from '../domain/threatAssessment.js';
import { createPoller } from './poller.js';

const DEFAULT_POLL_INTERVAL_MS = 10_000;

/** Data sources of the initial load that may fail without blocking the dashboard (settings is the only hard dependency). */
export type LoadSource = 'sites' | 'perimeters' | 'suspects' | 'protectedUsers' | 'countries';

export interface LoadFailure {
  source: LoadSource;
  error: unknown;
}

/** Resolves to the fetched value, or records the failure and resolves to `fallback` so one bad source never sinks the load. */
async function settle<T>(source: LoadSource, promise: Promise<T>, fallback: T, failures: Array<LoadFailure>): Promise<T> {
  try {
    return await promise;
  } catch (error) {
    failures.push({ source, error });
    return fallback;
  }
}

export interface UseLiveDataOptions {
  /** Load and poll only while true (logged in and connected); data is cleared when it turns false. */
  enabled: boolean;
  pollIntervalMs?: number;
  /** Current settings from the settings context (null before the first load). */
  settings: AppSettings | null;
  /** Suspects the analyst marked as not hostile. */
  notHostileIds: ReadonlyArray<string>;
  /** Called for every perimeter entry / breach, with the settings in force for that update. */
  onEvent: (event: ThreatEvent, settings: AppSettings) => void;
  /**
   * Called once per initial load that produced settings, after the first assessment.
   * `failures` lists the sources that fell back to empty data (the dashboard still renders).
   */
  onInitialLoad: (
    settings: AppSettings,
    state: ThreatState,
    countries: Array<CountryOption>,
    failures: Array<LoadFailure>
  ) => void;
  /** Called when the initial load fails hard (settings could not be loaded). */
  onError: (err: unknown) => void;
  /** Called after every successful poll. */
  onPoll?: () => void;
  /** Called when a poll fails (every failed poll; the error is also logged). */
  onPollError?: (err: unknown) => void;
  /** Changing this value (e.g. the selected country) cancels the poller and reruns the initial load. */
  reloadKey?: string;
}

export interface ApplyOverrides {
  sites?: Array<Site>;
  perimeters?: Array<PerimeterConfig>;
  profiles?: Array<ThreatProfile>;
  settings?: AppSettings;
}

/** Keeps a ref in sync with the latest value, so long-lived callbacks never see stale props. */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

/**
 * Owns the live data slices (sites, perimeters, threat profiles, protected users, suspects, positions),
 * performs the initial load and the periodic poll, and runs the threat assessment outside React
 * updaters so its events can trigger UI side effects safely.
 */
export function useLiveData({
  enabled,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  settings,
  notHostileIds,
  onEvent,
  onInitialLoad,
  onError,
  onPoll,
  onPollError,
  reloadKey,
}: UseLiveDataOptions) {
  const [sites, setSites] = useState<Array<Site>>([]);
  const [perimeters, setPerimeters] = useState<Array<PerimeterConfig>>([]);
  const [threatProfiles, setThreatProfiles] = useState<Array<ThreatProfile>>([]);
  const [protectedUsers, setProtectedUsers] = useState<Array<ProtectedUser>>([]);
  const [suspects, setSuspects] = useState<Record<string, Suspect>>({});
  const [positions, setPositions] = useState<Record<string, Position>>({});
  /** True from the start of a (re)load until the initial data arrived or failed. */
  const [loading, setLoading] = useState(false);

  // Mirrors of the slices for the poller, so it never re-subscribes when they change.
  const sitesRef = useLatest(sites);
  const perimetersRef = useLatest(perimeters);
  const profilesRef = useLatest(threatProfiles);
  const positionsRef = useLatest(positions);
  const settingsRef = useLatest(settings);
  const notHostileIdsRef = useLatest(notHostileIds);
  const callbacks = useLatest({ onEvent, onInitialLoad, onError, onPoll, onPollError });
  const breachFiredRef = useRef<Record<string, boolean>>({});

  /** Runs the threat assessment on fresh suspects and publishes the result (state, refs, events). */
  const apply = useCallback(
    (nextSuspects: Record<string, Suspect>, overrides: ApplyOverrides = {}): ThreatState => {
      const next = computeThreatState({
        suspects: nextSuspects,
        prevPositions: positionsRef.current,
        sites: overrides.sites ?? sitesRef.current,
        perimeters: overrides.perimeters ?? perimetersRef.current,
        profiles: overrides.profiles ?? profilesRef.current,
        notHostileIds: notHostileIdsRef.current,
        breachFired: breachFiredRef.current,
      });

      positionsRef.current = next.positions;
      sitesRef.current = next.sites;
      profilesRef.current = next.profiles;
      breachFiredRef.current = next.breachFired;

      setPositions(next.positions);
      setSites(next.sites);
      setThreatProfiles(next.profiles);
      setSuspects(next.suspects);

      const eventSettings = overrides.settings ?? settingsRef.current;
      if (eventSettings) {
        next.events.forEach(event => callbacks.current.onEvent(event, eventSettings));
      }
      return next;
    },
    [positionsRef, sitesRef, perimetersRef, profilesRef, notHostileIdsRef, settingsRef, callbacks]
  );

  useEffect(() => {
    if (!enabled) {
      setSuspects({});
      setPositions({});
      setLoading(false);
      positionsRef.current = {};
      breachFiredRef.current = {};
      return;
    }

    // A (re)load starts from a clean slate so positions/trails of a previous scope never leak in.
    positionsRef.current = {};
    breachFiredRef.current = {};
    setLoading(true);

    let cancelled = false;
    const poller = createPoller(
      () => Promise.all([fetchSuspects(false), fetchProtectedUsers()]),
      ([nextSuspects, nextProtectedUsers]) => {
        if (cancelled) return;
        apply(nextSuspects);
        setProtectedUsers(nextProtectedUsers);
        callbacks.current.onPoll?.();
      },
      pollIntervalMs,
      {
        immediate: false,
        onError: err => {
          console.error('Polling error:', err);
          if (!cancelled) callbacks.current.onPollError?.(err);
        },
      }
    );

    (async () => {
      try {
        // Settings are the only hard dependency: the dashboard cannot render without them.
        // Every other source falls back to empty data and is reported through `failures`.
        const failures: Array<LoadFailure> = [];
        const [
          sitesData,
          { perimeters: perimetersData, threatProfiles: profilesData },
          suspectsData,
          settingsData,
          users,
          countriesData,
        ] = await Promise.all([
          settle('sites', fetchSites(), [] as Array<Site>, failures),
          settle('perimeters', fetchPerimeters(), { perimeters: [], threatProfiles: [] }, failures),
          settle('suspects', fetchSuspects(true), {} as Record<string, Suspect>, failures),
          fetchSettings(),
          settle('protectedUsers', fetchProtectedUsers(), [] as Array<ProtectedUser>, failures),
          settle('countries', fetchCountries(), [] as Array<CountryOption>, failures),
        ]);
        if (cancelled) return;

        perimetersRef.current = perimetersData;
        setPerimeters(perimetersData);
        setProtectedUsers(users);
        const state = apply(suspectsData, {
          sites: sitesData,
          perimeters: perimetersData,
          profiles: profilesData,
          settings: settingsData,
        });
        setLoading(false);
        callbacks.current.onInitialLoad(settingsData, state, countriesData, failures);
        poller.start();
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        callbacks.current.onError(err);
      }
    })();

    return () => {
      cancelled = true;
      poller.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reloadKey is a deliberate trigger
  }, [enabled, pollIntervalMs, apply, callbacks, perimetersRef, positionsRef, reloadKey]);

  return {
    sites,
    setSites,
    perimeters,
    setPerimeters,
    threatProfiles,
    setThreatProfiles,
    protectedUsers,
    suspects,
    setSuspects,
    positions,
    loading,
    apply,
  };
}
